import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useChat } from '@ai-sdk/react';
import { useApolloClient } from '@apollo/client/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai';
import type { UIMessage } from 'ai';
import { DEVICE_TROUBLESHOOT_SESSIONS_QUERY } from '@scio3/graphql-client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import type { DeviceTroubleshootSessionSummary } from './useDeviceTroubleshootHistory';
import { getChatApiUrl, CHAT_REQUEST_TIMEOUT_MS } from '../utils/constants';
import { ChatError, classifyOptiBotError, getErrorI18nKey } from '../utils/error-classification';
import { getViewContext } from '../utils/view-context';
import {
  getOrCreateConversationId,
  resetConversationId,
  setConversationId,
} from '../utils/conversation-id';

const CHAT_API_URL = getChatApiUrl();

/**
 * Module-level pending image IDs for the next chat request.
 * Written by OptiBotPanel before sendMessage, read by the transport body callback,
 * then cleared by the transport fetch callback after the request fires.
 */
let _pendingImageIds: string[] = [];

/** Set image IDs to include in the next chat request body. */
export function setActiveImageIds(ids: string[]) {
  _pendingImageIds = ids;
}

/** sessionStorage key scoped by teamId so switching teams starts fresh */
function getSessionKey(teamId: string) {
  return `optibot-session-${teamId}`;
}

function getThreadIdKey(teamId: string) {
  return `optibot-threadId-${teamId}`;
}

function getRequestIdKey(teamId: string) {
  return `optibot-requestIds-${teamId}`;
}

/**
 * Messages are cached across reloads but their Braintrust span ids were not, so feedback on
 * any restored message silently no-oped. Persist the map alongside them.
 */
function readRequestIds(teamId: string): Map<string, string> {
  try {
    const raw = sessionStorage.getItem(getRequestIdKey(teamId));
    return raw ? new Map(JSON.parse(raw) as [string, string][]) : new Map();
  } catch {
    return new Map();
  }
}

function writeRequestIds(teamId: string, map: Map<string, string>) {
  try {
    sessionStorage.setItem(getRequestIdKey(teamId), JSON.stringify([...map]));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

/** Read cached messages from sessionStorage */
function readCachedMessages(teamId: string): UIMessage[] | undefined {
  try {
    const raw = sessionStorage.getItem(getSessionKey(teamId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as UIMessage[];
    if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/** Persist messages to sessionStorage (strips file parts to avoid 5MB limit bloat) */
function writeCachedMessages(teamId: string, messages: UIMessage[]) {
  try {
    const stripped = messages.map((msg) => ({
      ...msg,
      parts: msg.parts.filter((p) => p.type !== 'file'),
    }));
    sessionStorage.setItem(getSessionKey(teamId), JSON.stringify(stripped));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

export function useOptiBot() {
  const { t } = useTranslation();
  const teamId = useTeamStore((s) => s.activeTeamId);
  const apolloClient = useApolloClient();

  const [input, setInput] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Restore activeThreadId from sessionStorage when teamId becomes available
  useEffect(() => {
    if (!teamId) return;
    const stored = sessionStorage.getItem(getThreadIdKey(teamId));
    if (stored) setActiveThreadId(stored);
  }, [teamId]);

  /** Maps assistant messageId → X-Request-Id from the backend response header. */
  const requestIdMapRef = useRef(new Map<string, string>());

  // Restore the span-id map when teamId becomes available, so feedback survives a reload.
  useEffect(() => {
    if (!teamId) return;
    requestIdMapRef.current = readRequestIds(teamId);
  }, [teamId]);

  /** Maps messageId → timestamp (first-seen time). */
  const messageTimestampRef = useRef(new Map<string, Date>());

  // Read token fresh from the store at request time (not from a stale closure).
  // This avoids 401s when Firebase refreshes the token after the transport was created.
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: CHAT_API_URL,
        headers: () => ({ Authorization: `Bearer ${useAuthStore.getState().token}` }),
        body: () => ({
          teamId: useTeamStore.getState().activeTeamId,
          conversationId: getOrCreateConversationId(useTeamStore.getState().activeTeamId),
          viewContext: getViewContext(),
          ...(_pendingImageIds.length > 0 ? { uploadedImageIds: [..._pendingImageIds] } : {}),
        }),
        fetch: async (url, init) => {
          // Clear pending image IDs after the request is sent
          _pendingImageIds = [];

          // Merge SDK abort signal with a timeout signal
          const timeoutSignal = AbortSignal.timeout(CHAT_REQUEST_TIMEOUT_MS);
          const combinedSignal = init?.signal
            ? AbortSignal.any([init.signal, timeoutSignal])
            : timeoutSignal;

          const response = await fetch(url, { ...init, signal: combinedSignal });

          if (!response.ok) {
            throw new ChatError(`Chat request failed (${response.status})`, response.status);
          }

          const requestId = response.headers.get('X-Request-Id');
          if (requestId) {
            // Store temporarily — will be mapped to the assistant message ID in onFinish
            requestIdMapRef.current.set('__latest__', requestId);
          }
          return response;
        },
      }),
    [],
  );

  // Restore cached messages from sessionStorage (team-scoped)
  const cachedMessages = useMemo(() => readCachedMessages(teamId), [teamId]);

  const { messages, sendMessage, setMessages, status, error, stop, addToolApprovalResponse } =
    useChat({
      transport,
      messages: cachedMessages,
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
      onFinish: ({ messages: updatedMessages }) => {
        writeCachedMessages(teamId, updatedMessages);
        // Map the captured X-Request-Id to the last assistant message
        const latestRequestId = requestIdMapRef.current.get('__latest__');
        if (latestRequestId) {
          const lastAssistant = updatedMessages.filter((m) => m.role === 'assistant').pop();
          if (lastAssistant) {
            requestIdMapRef.current.set(lastAssistant.id, latestRequestId);
          }
          requestIdMapRef.current.delete('__latest__');
          writeRequestIds(teamId, requestIdMapRef.current);
        }
      },
      onError: (error) => {
        const errorType = classifyOptiBotError(error);
        toast.error(t(getErrorI18nKey(errorType)));
      },
    });

  const isLoading = status === 'submitted' || status === 'streaming';

  // Track first-seen timestamps for each message
  useEffect(() => {
    const tsMap = messageTimestampRef.current;
    for (const msg of messages) {
      if (!tsMap.has(msg.id)) {
        // createdAt is set on history-loaded messages (not in UIMessage type)
        const raw = 'createdAt' in msg ? msg.createdAt : undefined;
        const created = raw ? new Date(raw as string | Date) : undefined;
        tsMap.set(msg.id, created ?? new Date());
      }
    }
  }, [messages]);

  /** Get timestamp for a message by ID */
  const getMessageTimestamp = useCallback((messageId: string): Date | undefined => {
    return messageTimestampRef.current.get(messageId);
  }, []);

  /** Stop streaming and clean up incomplete tool invocations from messages.
   *  Without cleanup, the next sendMessage would include incomplete tool_use blocks
   *  (no matching tool_result), causing the backend to reject the request silently.
   *  Replaces removed tool parts with a "stopped" text so the message isn't orphaned. */
  const handleStop = useCallback(() => {
    stop();
    const stoppedPart = { type: 'text' as const, text: '{{STOPPED}}' };
    setMessages((prev) => {
      const lastIdx = prev.length - 1;
      if (lastIdx < 0) return prev;
      const lastMsg = prev[lastIdx];

      // Case 1: Stop before response started — last message is user, add assistant placeholder
      if (lastMsg.role === 'user') {
        return [
          ...prev,
          { id: crypto.randomUUID(), role: 'assistant' as const, parts: [stoppedPart] },
        ];
      }

      // Case 2: Stop during streaming — keep only parts that actually render in the
      // bubble (non-empty text or a tool invocation). This drops incomplete tool
      // parts (would orphan tool_use blocks on resend), empty text placeholders, and
      // non-rendering markers like step-start that exist before the first token.
      if (lastMsg.role === 'assistant') {
        const isRenderable = (part: (typeof lastMsg.parts)[number]) => {
          if (
            'state' in part &&
            (part.state === 'input-streaming' || part.state === 'input-available')
          ) {
            return false;
          }
          if (part.type === 'text') return part.text.trim() !== '';
          return part.type === 'dynamic-tool' || part.type.startsWith('tool-');
        };
        const renderableParts = lastMsg.parts.filter(isRenderable);
        // No real content was produced → show only "Response stopped".
        if (renderableParts.length === 0) {
          return [...prev.slice(0, lastIdx), { ...lastMsg, parts: [stoppedPart] }];
        }
        // Had content but we trimmed incomplete/non-rendering parts → keep content
        // and append the sentinel so the truncation reads as "Response stopped".
        if (renderableParts.length !== lastMsg.parts.length) {
          return [
            ...prev.slice(0, lastIdx),
            { ...lastMsg, parts: [...renderableParts, stoppedPart] },
          ];
        }
        // Nothing to change.
        return prev;
      }

      return prev;
    });
  }, [stop, setMessages]);

  /** Clear session and reset chat to empty state */
  const clearChat = useCallback(() => {
    sessionStorage.removeItem(getSessionKey(teamId));
    sessionStorage.removeItem(getThreadIdKey(teamId));
    sessionStorage.removeItem(getRequestIdKey(teamId));
    resetConversationId(teamId);
    requestIdMapRef.current.clear();
    messageTimestampRef.current.clear();
    setActiveThreadId(null);
    setMessages([]);
  }, [teamId, setMessages]);

  /** Tracks which thread we've already rebuilt session cards for (avoids a redundant re-query). */
  const rebuiltThreadRef = useRef<string | null>(null);

  /**
   * Rebuild the device-troubleshoot session cards that a transcript reload drops. HubSpot stores only the
   * text transcript, and the cached same-session copy is text-only too — so the structured tool part that
   * renders the card is lost. open-api stamps each session with the conversation `threadId`; here we fetch
   * the account's sessions (the same authorized query the session-list uses), keep those for THIS thread,
   * and attach a card to the assistant message nearest each session's `startedAt`. Cards already present
   * (live, in-memory) are deduped by sessionId. Returns the SAME array ref when nothing was added.
   */
  const rebuildSessionCards = useCallback(
    async (msgs: UIMessage[], threadId: string): Promise<UIMessage[]> => {
      try {
        const { data } = await apolloClient.query<{
          deviceTroubleshootSessions: DeviceTroubleshootSessionSummary[];
        }>({ query: DEVICE_TROUBLESHOOT_SESSIONS_QUERY, fetchPolicy: 'network-only' });
        const sessions = (data?.deviceTroubleshootSessions ?? []).filter(
          (s) => s.threadId === threadId,
        );
        if (!sessions.length) return msgs;

        // sessionIds already rendered as a card (live in-memory) → don't duplicate
        const existing = new Set<string>();
        for (const m of msgs) {
          for (const p of m.parts) {
            const tp = p as { type?: string; output?: { sessionId?: unknown } };
            if (
              tp.type === 'tool-openDeviceTroubleshootSession' &&
              typeof tp.output?.sessionId === 'string'
            ) {
              existing.add(tp.output.sessionId);
            }
          }
        }
        const missing = sessions.filter((s) => !existing.has(s.sessionId));
        if (!missing.length) return msgs;

        const next = msgs.map((m) => ({ ...m, parts: [...m.parts] }));
        // One thread can hold MANY sessions. Assign each to a DISTINCT assistant message (so two cards never
        // pile on one message), pairing in chronological order — earliest session → its nearest free message.
        const usedIdx = new Set<number>();
        const ordered = [...missing].sort(
          (a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt),
        );
        for (const s of ordered) {
          const cardPart = {
            type: 'tool-openDeviceTroubleshootSession',
            toolCallId: `history-${s.sessionId}`,
            state: 'output-available',
            output: {
              sessionId: s.sessionId,
              sessionToken: '', // history mode opens by sessionId — live token not needed for an ended session
              streamUrl: '',
              deviceName: s.deviceName ?? undefined,
              title: s.title ?? undefined,
              startedAt: s.startedAt, // shown on the card → distinguishes multiple sessions on the same thread
            },
          } as unknown as UIMessage['parts'][number];
          // Placement: history messages carry createdAt; live/cached messages fall back to messageTimestampRef.
          const startMs = Date.parse(s.startedAt);
          let bestIdx = -1;
          let bestDelta = Number.POSITIVE_INFINITY;
          next.forEach((m, i) => {
            if (m.role !== 'assistant' || usedIdx.has(i)) return;
            const created =
              (m as { createdAt?: Date }).createdAt?.getTime() ??
              messageTimestampRef.current.get(m.id)?.getTime() ??
              Number.NaN;
            const delta = Math.abs(created - startMs);
            if (delta < bestDelta) {
              bestDelta = delta;
              bestIdx = i;
            }
          });
          if (bestIdx >= 0) {
            usedIdx.add(bestIdx);
            // card above the agent's text, mirroring the live layout
            next[bestIdx] = { ...next[bestIdx], parts: [cardPart, ...next[bestIdx].parts] };
          } else {
            next.push({
              id: crypto.randomUUID(),
              role: 'assistant',
              parts: [cardPart],
              createdAt: new Date(s.startedAt),
            } as UIMessage);
          }
        }
        return next;
      } catch {
        return msgs; // best-effort — conversation still loads without the cards
      }
    },
    [apolloClient],
  );

  /** Load a previous conversation by threadId from the backend */
  const loadConversation = useCallback(
    async (threadId: string) => {
      setIsLoadingHistory(true);
      try {
        const response = await globalThis.fetch(`${CHAT_API_URL}/history/${threadId}`, {
          headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
        });
        if (!response.ok) {
          toast.error(t('optibot.error'));
          return;
        }
        const data = (await response.json()) as {
          role: string;
          content: string;
          createdAt: string;
          isAgentReply?: boolean;
        }[];
        // Filter out HubSpot system/metadata messages and non-chat roles
        const filtered = data.filter((msg) => {
          if (msg.role !== 'user' && msg.role !== 'assistant') return false;
          const text = msg.content.trim();
          if (!text) return false;
          if (/^First Name:/i.test(text)) return false;
          if (/OptiBot conversation started by/i.test(text)) return false;
          if (/^\[Escalation Summary\]/i.test(text)) return false;
          if (/^\[System\]/i.test(text)) return false;
          return true;
        });
        // Backend returns newest-first — reverse to chronological order
        filtered.reverse();
        const uiMessages: UIMessage[] = filtered.map((msg) => ({
          id: crypto.randomUUID(),
          role: msg.role as 'user' | 'assistant',
          parts: [{ type: 'text' as const, text: msg.content }],
          createdAt: new Date(msg.createdAt),
          ...(msg.isAgentReply ? { metadata: { isAgentReply: true } } : {}),
        }));

        const rebuilt = await rebuildSessionCards(uiMessages, threadId);

        requestIdMapRef.current.clear();
        sessionStorage.removeItem(getRequestIdKey(teamId));
        setActiveThreadId(threadId);
        setConversationId(teamId, threadId);
        sessionStorage.setItem(getThreadIdKey(teamId), threadId);
        rebuiltThreadRef.current = threadId; // already rebuilt → the mount effect skips a redundant re-query
        setMessages(rebuilt);
        writeCachedMessages(teamId, rebuilt);
      } catch {
        toast.error(t('optibot.error'));
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [teamId, setMessages, t, rebuildSessionCards],
  );

  // Rebuild session cards for the RESTORED (cached / same-session) conversation. The live in-memory messages
  // carry the card, but the sessionStorage copy is text-only — so on a fresh mount we re-attach the cards
  // from the session store. Runs once per thread; skips while a turn is streaming and when loadConversation
  // already rebuilt this thread.
  useEffect(() => {
    if (!teamId || !activeThreadId || isLoading) return;
    if (rebuiltThreadRef.current === activeThreadId || !messages.length) return;
    rebuiltThreadRef.current = activeThreadId;
    void rebuildSessionCards(messages, activeThreadId).then((rebuilt) => {
      if (rebuilt !== messages) {
        setMessages(rebuilt);
        writeCachedMessages(teamId, rebuilt);
      }
    });
  }, [teamId, activeThreadId, isLoading, messages, rebuildSessionCards, setMessages]);

  const handleApprove = useCallback(
    (toolCallId: string) => {
      addToolApprovalResponse({ id: toolCallId, approved: true });
    },
    [addToolApprovalResponse],
  );

  const handleDeny = useCallback(
    (toolCallId: string) => {
      addToolApprovalResponse({ id: toolCallId, approved: false });
    },
    [addToolApprovalResponse],
  );

  /** Fire-and-forget feedback submission to the backend. */
  const handleFeedback = useCallback(async (messageId: string, score: number, comment?: string) => {
    const requestId = requestIdMapRef.current.get(messageId);
    if (!requestId) return;

    try {
      const feedbackUrl = `${CHAT_API_URL}/feedback`;
      await globalThis.fetch(feedbackUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${useAuthStore.getState().token}`,
        },
        body: JSON.stringify({
          messageId: requestId,
          score,
          teamId: useTeamStore.getState().activeTeamId,
          ...(comment ? { comment } : {}),
        }),
      });
    } catch {
      // Fire-and-forget — silently ignore errors
    }
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    isLoadingHistory,
    activeThreadId,
    error,
    status,
    stop: handleStop,
    handleApprove,
    handleDeny,
    handleFeedback,
    clearChat,
    loadConversation,
    getMessageTimestamp,
    input,
    setInput,
  };
}
